import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { klikOpKop, sorteerbaar, type Context, type Lijst, type Sortering } from '@xxtract/tabelfilters';
import { RICHTING_KEYS } from './SorteerbareKop';
import { CONTROL_CLASS, FOCUS_CLASS } from './stijl';

const WISSEL_ICON = 'swap_vert';
const WEG_ICON = 'close';

interface SorteerKeuzeProps<R, C extends Context> {
  lijst: Lijst<R, C>;
  sortering: Sortering;
  onChange: (sortering: Sortering) => void;
  context?: C;
}

const knopClass = `inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-control)] text-[var(--foreground-muted)] hover:text-[var(--foreground)] ${FOCUS_CLASS}`;

/** Smal scherm (§5, CAP-15): de sorteerreeks als genummerde lijst, met wisselen, weghalen en toevoegen. */
export function SorteerKeuze<R, C extends Context>({ lijst, sortering, onChange, context }: SorteerKeuzeProps<R, C>) {
  const { t } = useTranslation();
  const veldId = useId();
  const kop = (sleutel: string) => {
    const k = lijst.kolommen.find((x) => x.sleutel === sleutel);
    return k ? t(k.kopSleutel) : sleutel;
  };
  const beschikbaar = lijst.kolommen.filter(
    (k) => sorteerbaar(k) && (context ? (k.zichtbaar?.(context) ?? true) : true) && !sortering.some((s) => s.sleutel === k.sleutel),
  );

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-[var(--foreground-muted)]">{t('tabel.sorteerOp')}</span>
      {sortering.length === 0 ? (
        <p className="text-sm text-[var(--foreground-muted)]">{t('tabel.standaardVolgorde')}</p>
      ) : (
        <ol className="flex flex-col gap-1">
          {sortering.map((s, i) => (
            <li className="flex items-center gap-2 text-sm text-[var(--foreground)]" key={s.sleutel}>
              <span className="flex flex-1 flex-wrap gap-x-2">
                <span className="tabular-nums text-[var(--foreground-muted)]">{i + 1}</span>
                <span>{kop(s.sleutel)}</span>
                <span className="text-[var(--foreground-muted)]">{t(RICHTING_KEYS[s.richting])}</span>
              </span>
              <button
                aria-label={t('tabel.richtingWisselen', { kolom: kop(s.sleutel) })}
                className={knopClass}
                onClick={() => onChange(sortering.map((x, j) => (j === i ? { ...x, richting: x.richting === 'op' ? 'af' : 'op' } : x)))}
                type="button"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[18px]">{WISSEL_ICON}</span>
              </button>
              <button
                aria-label={t('tabel.sorteringWeghalen', { kolom: kop(s.sleutel) })}
                className={knopClass}
                onClick={() => onChange(sortering.filter((_, j) => j !== i))}
                type="button"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[18px]">{WEG_ICON}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
      {beschikbaar.length > 0 ? (
        <>
          <label className="sr-only" htmlFor={veldId}>{t('tabel.sorteringToevoegen')}</label>
          <select
            className={`${CONTROL_CLASS} w-full`}
            id={veldId}
            onChange={(e) => {
              const k = lijst.kolommen.find((x) => x.sleutel === e.target.value);
              if (k) onChange(klikOpKop(sortering, k, lijst.maxSorteerkolommen ?? 5));
            }}
            value=""
          >
            <option value="">{t('tabel.sorteringToevoegen')}</option>
            {beschikbaar.map((k) => (
              <option key={k.sleutel} value={k.sleutel}>{t(k.kopSleutel)}</option>
            ))}
          </select>
        </>
      ) : null}
    </div>
  );
}
